/* global JQuery */
import {createApp} from 'vue';
import {getPinia} from '../allspice_app/stores/pinia.ts';

import AllSpiceDiff from '../components/AllSpiceDiff/AllSpiceDiff.vue';
import AllSpiceDiffSnippet from '../components/AllSpiceDiff/AllSpiceDiffSnippet.vue';
import PcbLayerToggle from '../components/PcbLayerToggle.vue';
import VariantDropdown from '../components/VariantDropdown.vue';
import {globalContainer} from '../container.ts';
import {isJQuery} from '../domutils.ts';
import AllSpiceDiffToggle from '../components/AllSpiceDiff/AllSpiceDiffToggle.vue';
import AllSpiceFlipToggle from '../components/AllSpiceDiff/AllSpiceFlipToggle.vue';
import AllSpiceBillingForm from '../components/AllSpiceBillingForm.vue';
import AllSpiceBillingCommitterSettings from '../components/AllSpiceBillingCommitterSettings.vue';
import AllSpiceBillingActionsSettings from '../components/AllSpiceBillingActionsSettings.vue';
import AllSpiceBillingSuccess from '../components/AllSpiceBillingSuccess.vue';
import DiffFilter from '../components/AllSpiceDiff/DiffFilter.vue';
import DocumentSearch from '../components/DocumentSearch.vue';
import AllSpiceOnboarding from '../components/AllSpiceOnboarding/Onboarding.vue';
import AllSpiceAccordion from '../components/AllSpiceAccordion.vue';
import ReplacementDetectionPage from '../allspice_app/pages/ReplacementDetectionPage/ReplacementDetectionPage.vue';
import WarningMessages from '../components/WarningMessages.vue';
import SmartReviewControls from '../components/SmartReviewControls.vue';
import DrcyChat from '../components/DrcyChat.vue';
import {SvgIcon} from '../svg.ts';

type Parent = Document | Element | Array<Document | Element> | JQuery<HTMLElement>;
type ParentElement = Document | Element;

export async function initAllSpice(parent: Parent = [document]) {
  let parentElements: ParentElement[];
  // Handle Element or jQuery object.
  if (isJQuery(parent)) {
    // Convert to Array.
    parentElements = (parent as JQuery<HTMLElement>).get();
  } else if (!Array.isArray(parent)) {
    parentElements = [parent as ParentElement];
  } else {
    parentElements = parent;
  }

  for (const parentElem of parentElements) {
    const els = Array.from(parentElem.querySelectorAll('.allspice-vue-mount'));

    for (const el of els) {
      mountElement(el);
    }

    const parentSafeEls = Array.from(parentElem.querySelectorAll('allspice-diff-snippet'));

    for (const el of parentSafeEls) {
      mountElement(el, true);
    }
  }
}

/**
 * @param {Element} el
 * @param {boolean} useParent
 */
function mountElement(el: Element, useParent = false) {
  // Don't try to mount an element we've mounted before.
  if (isElementMounted(useParent ? el.parentNode : el)) {
    return;
  }

  if (!useParent) {
    // TODO: Don't add a sibling once we have removed vueInstance()
    el.append(document.createElement('div'));
  }

  const app = createApp({});
  app
    .component('allspice-diff', AllSpiceDiff)
    .component('allspice-diff-snippet', AllSpiceDiffSnippet)
    .component('pcb-layer-toggle', PcbLayerToggle)
    .component('variant-dropdown', VariantDropdown)
    .component('allspice-flip-toggle', AllSpiceFlipToggle)
    .component('allspice-diff-toggle', AllSpiceDiffToggle)
    .component('allspice-billing-form', AllSpiceBillingForm)
    .component('allspice-billing-committer-settings', AllSpiceBillingCommitterSettings)
    .component('allspice-billing-actions-settings', AllSpiceBillingActionsSettings)
    .component('allspice-billing-success', AllSpiceBillingSuccess)
    .component('allspice-diff-filter', DiffFilter)
    .component('document-search', DocumentSearch)
    .component('allspice-onboarding', AllSpiceOnboarding)
    .component('allspice-accordion', AllSpiceAccordion)
    .component('svg-icon', SvgIcon)
    .component('replacement-detection', ReplacementDetectionPage)
    .component('warning-messages', WarningMessages)
    .component('smart-review-controls', SmartReviewControls)
    .component('drcy-chat', DrcyChat);
  app
    .use(getPinia())
    // Provide our dependency-injection container to Vue.
    .provide('container', globalContainer)
    // For convenience, eagerly provide things from the application
    // container so that Vue components can use Vue's `inject`, instead of
    // having to go through our container.  Unfortunately, Vue's dependency
    // injection is eager, so you may not want to do this for expensive
    // objects.
    .provide('fetcher', globalContainer.lookup('fetcher'));
  app.mixin({
    mounted() {
      this.$el.__vueComponent = this;
    },
  });

  if (useParent) {
    app.mount(el.parentNode as Element);
  } else {
    app.mount(el);
  }
}

/**
 * Returns true if the given element has been mounted as a top-level Vue app.
 * @param {Node | null | undefined} el
 * @returns {boolean}
 */
function isElementMounted(el: Node | null | undefined) {
  return el && '__vue_app__' in el && Boolean(el.__vue_app__);
}
